import React, { useState } from 'react'
import { User } from '../../models/User'
import { UserConsent, UserConsentState } from './model'
import { ConsentAvatar } from './ConsentAvatar'
import { ConsentStateDropdown } from './ConsentStateDropdown'
import { ConsentConfirmationModal } from './ConsentConfirmationModal'
import { EvidenceUploadConfig, CommentUploadConfig } from '../../models/Account'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { useUpdateExternalUserConsentMutation } from '../../store/externalUser/externalUser'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

type PendingConsent = {
  user: User
  newState: UserConsentState
}

type MultiContactConsentListProps = {
  clients: User[]
  usersConsent: Record<string, UserConsent | null> | null
  evidenceUploadConfig: EvidenceUploadConfig
  commentUploadConfig: CommentUploadConfig
  manualConsentEnabled: boolean
  roomId?: string
  sessionId?: string
}

export const MultiContactConsentList: React.FC<MultiContactConsentListProps> = ({
  clients,
  usersConsent,
  evidenceUploadConfig,
  commentUploadConfig,
  manualConsentEnabled,
  roomId,
  sessionId,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(null)
  const [localConsentStates, setLocalConsentStates] = useState<Record<string, UserConsentState>>({})

  const [updateConsent, { isLoading: isSaving }] = useUpdateExternalUserConsentMutation()
  const { tracked } = useTracking()

  const requiresConfirmationModal = evidenceUploadConfig.enabled || commentUploadConfig.enabled

  const getConsentState = (user: User): UserConsentState => {
    if (localConsentStates[user.id]) return localConsentStates[user.id]
    return usersConsent?.[user.id]?.current?.state ?? UserConsentState.UNKNOWN
  }

  const handleConsentChange = (user: User, newState: UserConsentState) => {
    if (requiresConfirmationModal) {
      setPendingConsent({ user, newState })
    } else {
      applyConsentChange(user, newState)
    }
  }

  const applyConsentChange = tracked(
    async (user: User, newState: UserConsentState, params?: { reason?: string; attachmentFileId?: string }) => {
      setLocalConsentStates((prev) => ({ ...prev, [user.id]: newState }))
      await updateConsent({
        accountId,
        userId: user.id,
        newValue: newState,
        type: 'manual',
        roomId,
        sessionId,
        reason: params?.reason,
        attachmentFileId: params?.attachmentFileId,
      })
    },
    {
      eventName: TrackEvent.CONSENT_CHANGED,
    },
    {
      eventDetailsExtractor: (user: User, newState: UserConsentState) => ({
        user_id: user.id,
        new_value: newState,
        session_id: sessionId,
        room_id: roomId,
        source: 'locked-per-client',
      }),
    },
  )

  const handleConfirmationSave = async (params: { reason?: string; attachmentFileId?: string }) => {
    if (!pendingConsent) return
    await applyConsentChange(pendingConsent.user, pendingConsent.newState, params)
    setPendingConsent(null)
  }

  const handleConfirmationCancel = () => {
    setPendingConsent(null)
  }

  return (
    <>
      <div className='d-flex flex-column'>
        {clients.map((client, index) => {
          const consentState = getConsentState(client)
          const displayName = getUserDisplayName(client)
          const isLast = index === clients.length - 1

          return (
            <div key={client.id} className={`d-flex align-items-center justify-content-between py-3 ${!isLast ? 'border-bottom' : ''}`}>
              <div className='d-flex align-items-center gap-2'>
                <ConsentAvatar name={displayName} consentState={consentState} />
                <span className='fw-bold paragraph-medium zocks-text-gray-900'>{displayName}</span>
              </div>
              {manualConsentEnabled ? (
                <ConsentStateDropdown value={consentState} onChange={(newState) => handleConsentChange(client, newState)} />
              ) : (
                <div className='border rounded-1 p-2 d-flex align-items-center gap-2'>
                  <span className='paragraph-small zocks-text-gray-600'>{consentState}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {pendingConsent ? (
        <ConsentConfirmationModal
          show={true}
          onCancel={handleConfirmationCancel}
          contactName={getUserDisplayName(pendingConsent.user)}
          consentState={pendingConsent.newState}
          onConsentStateChange={(newState) => setPendingConsent((prev) => (prev ? { ...prev, newState } : null))}
          evidenceUploadConfig={evidenceUploadConfig}
          commentUploadConfig={commentUploadConfig}
          onSave={handleConfirmationSave}
          isLoading={isSaving}
        />
      ) : null}
    </>
  )
}
